/**
 * The dispatch board — the daily heartbeat, and the answer to the owner's first question:
 * what are my technicians actually doing right now?
 *
 * Assignment is `job.assign`, whose rule is last-write-wins on server receipt (SYNC.md Rule
 * 5): "Dispatch is the manager's, and managers are online. If two managers reassign, the
 * later one at the server wins. The loser sees it change under them via the live feed." The
 * poll below is that live feed.
 *
 * Drag exists, but every card also carries a plain select, and the select is the real control
 * — the board is used all day by a dispatcher who may be on a trackpad with one hand on a
 * phone, and a drag that misses is a job assigned to the wrong man.
 */

import { useState } from "react";
import { useNavigate } from "react-router";
import { JOB_STATE_AR } from "@fanni/shared/job-state";
import { PRIORITY_AR, STATUS_TONE, fmtTime, isoDay, today } from "../lib/format.js";
import { BOARD_POLL_MS, useAssign, useBoard } from "../lib/queries.js";
import type { BoardCard, BoardTechnician } from "../lib/types.js";
import { Badge, Button, ErrorNote, Input, Loading, Note, PageHeader, cx } from "../ui/kit.js";

export function Board() {
  const [date, setDate] = useState(today());
  const { data, isLoading, error, isFetching } = useBoard(date);
  const assign = useAssign();
  const [dragId, setDragId] = useState<string | null>(null);

  const technicians = data?.columns.map((c) => c.technician) ?? [];

  const shift = (days: number) => {
    const d = new Date(`${date}T12:00:00`);
    d.setDate(d.getDate() + days);
    setDate(isoDay(d));
  };

  const onDrop = (technicianId: string | null) => {
    if (!dragId) return;
    assign.mutate({ jobId: dragId, technicianId });
    setDragId(null);
  };

  return (
    <>
      <PageHeader
        title="لوحة التوزيع"
        subtitle={
          <>
            تُحدَّث كل <span className="num">{BOARD_POLL_MS / 1000}</span> ثانية.
            {isFetching ? " جارٍ التحديث…" : ""}
          </>
        }
        actions={
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={() => shift(-1)}>
              ← اليوم السابق
            </Button>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-40"
            />
            <Button size="sm" onClick={() => shift(1)}>
              اليوم التالي →
            </Button>
            <Button size="sm" variant="default" onClick={() => setDate(today())}>
              اليوم
            </Button>
          </div>
        }
      />

      {error ? <ErrorNote error={error} className="mb-4" /> : null}
      {assign.error ? <ErrorNote error={assign.error} className="mb-4" /> : null}

      {isLoading ? (
        <Loading />
      ) : !data ? null : (
        <div className="scroll-x pb-2">
          <div className="flex min-w-max items-start gap-3">
            <Column
              title="غير مُسندة"
              tone="warn"
              count={data.unassigned.length}
              subtitle="هذه هي شغلة الموزّع"
              onDrop={() => onDrop(null)}
              dragging={Boolean(dragId)}
            >
              {data.unassigned.map((job) => (
                <JobCard
                  key={job.id}
                  job={job}
                  technicians={technicians}
                  onDragStart={() => setDragId(job.id)}
                  onAssign={(technicianId) => assign.mutate({ jobId: job.id, technicianId })}
                />
              ))}
            </Column>

            {data.columns.map((col) => (
              <Column
                key={col.technician.id}
                title={col.technician.name}
                count={col.jobs.length}
                subtitle={col.technician.phone}
                onDrop={() => onDrop(col.technician.id)}
                dragging={Boolean(dragId)}
              >
                {col.jobs.map((job) => (
                  <JobCard
                    key={job.id}
                    job={job}
                    technicians={technicians}
                    onDragStart={() => setDragId(job.id)}
                    onAssign={(technicianId) => assign.mutate({ jobId: job.id, technicianId })}
                  />
                ))}
              </Column>
            ))}

            {data.columns.length === 0 ? (
              <Note>لا يوجد فنيون فعّالون. أضِفهم من الإعدادات.</Note>
            ) : null}
          </div>
        </div>
      )}
    </>
  );
}

function Column({
  title,
  subtitle,
  count,
  tone,
  children,
  onDrop,
  dragging,
}: {
  title: string;
  subtitle?: string;
  count: number;
  tone?: "warn";
  children: React.ReactNode;
  onDrop: () => void;
  dragging: boolean;
}) {
  const [over, setOver] = useState(false);

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        onDrop();
      }}
      className={cx(
        "flex w-64 shrink-0 flex-col rounded-lg border bg-surface",
        tone === "warn" ? "border-warn/40" : "border-line",
        dragging && "border-dashed",
        over && "border-accent bg-accent-soft",
      )}
    >
      <header className="border-b border-line px-3 py-2">
        <div className="flex items-center justify-between gap-2">
          <p className="truncate text-sm font-semibold text-ink">{title}</p>
          <Badge tone={tone === "warn" && count > 0 ? "warn" : "neutral"}>
            <span className="num">{count}</span>
          </Badge>
        </div>
        {subtitle ? <p className="num truncate text-[11px] text-ink-faint">{subtitle}</p> : null}
      </header>
      <div className="min-h-24 space-y-2 p-2">
        {count === 0 ? <p className="py-6 text-center text-xs text-ink-faint">لا شيء</p> : children}
      </div>
    </div>
  );
}

function JobCard({
  job,
  technicians,
  onAssign,
  onDragStart,
}: {
  job: BoardCard;
  technicians: BoardTechnician[];
  onAssign: (technicianId: string | null) => void;
  onDragStart: () => void;
}) {
  const navigate = useNavigate();

  return (
    <article
      draggable
      onDragStart={onDragStart}
      className="rounded-md border border-line bg-surface p-2 hover:border-accent/40"
    >
      <div
        role="button"
        tabIndex={0}
        onClick={() => navigate(`/jobs/${job.id}`)}
        onKeyDown={(e) => {
          if (e.key === "Enter") navigate(`/jobs/${job.id}`);
        }}
        className="cursor-pointer"
      >
        <div className="mb-1 flex items-center justify-between gap-1">
          <Badge tone={STATUS_TONE[job.status]}>{JOB_STATE_AR[job.status]}</Badge>
          {job.priority !== "normal" ? (
            <Badge tone={job.priority === "emergency" ? "bad" : job.priority === "high" ? "warn" : "neutral"}>
              {PRIORITY_AR[job.priority]}
            </Badge>
          ) : null}
        </div>
        <p className="truncate text-sm font-medium text-ink">{job.customerName ?? "—"}</p>
        <p className="truncate text-xs text-ink-soft">{job.siteLabel ?? "—"}</p>
        <p className="mt-1 flex items-center justify-between text-[11px] text-ink-faint">
          <span className="num">{fmtTime(job.scheduledFor)}</span>
          {job.number ? <span className="num">#{job.number}</span> : null}
        </p>
      </div>

      {/* Correctness over drag flair: this select is the control that always works. */}
      <select
        value={job.technicianId ?? ""}
        onChange={(e) => onAssign(e.target.value || null)}
        onClick={(e) => e.stopPropagation()}
        aria-label="إسناد إلى فني"
        className="mt-2 w-full rounded border border-line bg-surface px-1.5 py-1 text-xs text-ink-soft focus:border-accent focus:outline-none"
      >
        <option value="">— غير مُسندة —</option>
        {technicians.map((t) => (
          <option key={t.id} value={t.id}>
            {t.name}
          </option>
        ))}
      </select>
    </article>
  );
}
